import { comboCards, RANKS_ASC } from './cards'
import type { StrategyNode } from '../types/solver'

export interface ExplanationSection {
  title: string
  body: string
  certainty: 'fact' | 'interpretation' | 'inference'
}

interface HandFeatures {
  madeHand: string
  draw: string | null
  blocker: string | null
}

function classifyHand(combo: string, board: string[]): HandFeatures {
  const [card1, card2] = comboCards(combo)
  const holeRanks = [RANKS_ASC.indexOf(card1[0]), RANKS_ASC.indexOf(card2[0])]
  const boardRanks = board.map((card) => RANKS_ASC.indexOf(card[0]))
  const maxBoard = Math.max(...boardRanks)
  const rankCounts = new Map<number, number>()
  ;[...holeRanks, ...boardRanks].forEach((rank) => rankCounts.set(rank, (rankCounts.get(rank) ?? 0) + 1))
  const matches = holeRanks.filter((rank) => boardRanks.includes(rank))
  const hasTrips = [...rankCounts.values()].some((count) => count >= 3)
  const twoPairRanks = [...rankCounts.values()].filter((count) => count >= 2).length

  let madeHand = 'ハイカード'
  if (hasTrips) madeHand = 'トリップス以上'
  else if (twoPairRanks >= 2) madeHand = 'ツーペア'
  else if (holeRanks[0] === holeRanks[1] && holeRanks[0] > maxBoard) madeHand = 'オーバーペア'
  else if (matches.includes(maxBoard)) madeHand = 'トップペア'
  else if (matches.length > 0 || holeRanks[0] === holeRanks[1]) madeHand = 'ワンペア'

  const suits = [...comboCards(combo), ...board].map((card) => card[1])
  const suitCounts = suits.reduce<Record<string, number>>((acc, suit) => ({ ...acc, [suit]: (acc[suit] ?? 0) + 1 }), {})
  const maxSuit = Math.max(...Object.values(suitCounts))
  const draw = maxSuit === 4 ? 'フラッシュドロー' : maxSuit === 3 && board.length === 3 ? 'バックドアフラッシュ候補' : null
  const boardSuitCounts = board.reduce<Record<string, number>>((acc, card) => ({ ...acc, [card[1]]: (acc[card[1]] ?? 0) + 1 }), {})
  const blockerCard = [card1, card2].find((card) => (boardSuitCounts[card[1]] ?? 0) >= 2)
  const blocker = blockerCard ? `${blockerCard}が同スートのブロッカーになり得ます` : null
  return { madeHand, draw, blocker }
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`
}

export function explainCombo(node: StrategyNode, combo: string): ExplanationSection[] {
  const strategy = node.rangeStrategy.combos[combo]
  if (!strategy) return []
  const features = classifyHand(combo, node.board)
  const actions = strategy.actions
    .map((action) => ({ ...action, definition: node.actions.find((candidate) => candidate.id === action.actionId)! }))
    .filter((action) => action.definition)
    .sort((a, b) => b.frequency - a.frequency)
  const main = actions[0]
  const secondary = actions[1]
  const finiteEvs = actions.filter((action) => Number.isFinite(action.ev))
  const evSpread = finiteEvs.length > 1
    ? Math.max(...finiteEvs.map((action) => action.ev!)) - Math.min(...finiteEvs.map((action) => action.ev!))
    : null
  const mixed = secondary && secondary.frequency >= 0.15

  const conclusion = mixed
    ? `${combo}は${main.definition.label}を${percent(main.frequency)}、${secondary.definition.label}を${percent(secondary.frequency)}で混ぜています。`
    : `${combo}は主に${main.definition.label}（${percent(main.frequency)}）を選びます。`

  const reasons = [`このハンドは${features.madeHand}です。`]
  if (features.draw) reasons.push(`${features.draw}も持っています。`)
  if (evSpread !== null) reasons.push(`表示アクション間のEV差は${evSpread.toFixed(3)}bbです。`)

  const role = main.definition.kind === 'bet' || main.definition.kind === 'raise' || main.definition.kind === 'all-in'
    ? 'ベット側に置かれる頻度が高く、バリュー獲得または相手のエクイティを降ろす役割を持つ可能性があります。'
    : main.definition.kind === 'check' || main.definition.kind === 'call'
      ? 'ショーダウン価値を残す、または受け側のレンジを支える役割を持つ可能性があります。'
      : '継続EVが低く、追加投資を避ける選択が中心です。'

  return [
    { title: '結論', body: conclusion, certainty: 'fact' },
    { title: '数値から分かること', body: reasons.join(' '), certainty: 'fact' },
    { title: 'ハンドの役割', body: role, certainty: 'inference' },
    ...(features.blocker ? [{ title: 'ブロッカー', body: features.blocker, certainty: 'interpretation' as const }] : []),
    { title: '注意点', body: '説明は計算結果とハンド特徴から作るルールベース解釈です。戦略の因果関係を断定するものではありません。', certainty: 'inference' },
  ]
}
